import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, UserPlus, Eye, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "./api";
import { useAuth } from "./AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const statusColor = (s) => {
  const map = {
    Active: "#22c55e", Probation: "#f59e0b", "On Leave": "#38bdf8",
    "Notice Period": "#fb923c", Resigned: "#94a3b8", Terminated: "#ef4444",
  };
  return map[s] || "#94a3b8";
};

export default function Employees() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/hr/employees", { params: q ? { search: q } : {} });
      setList(data);
    } catch {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 350);
    return () => clearTimeout(t);
  }, [search, load]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/hr/employees/${deleteTarget.employee_id}`);
      toast.success("Employee deleted");
      setDeleteTarget(null);
      load(search);
    } catch {
      toast.error("Failed to delete employee");
    }
  };

  return (
    <div data-testid="employees-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <span className="mono-label" style={{ color: "#00F0FF" }}>Employee Management</span>
          <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">Employees</h1>
        </div>
        {canEdit && (
          <Link to="/portal/employees/new" data-testid="add-employee-button"
            className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-5 py-3 mono-label transition-colors duration-300">
            <UserPlus size={16} /> Add Employee
          </Link>
        )}
      </div>

      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#64748B" }} />
        <input className="field !pl-11" placeholder="Search by name, ID, code, email, mobile..."
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="employee-search" />
      </div>

      <div className="bg-[#0C1322] border border-white/10 overflow-x-auto">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="animate-spin" style={{ color: "#00F0FF" }} /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-sm" style={{ color: "#64748B" }} data-testid="employees-empty">
            No employees found.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="border-b border-white/10 text-left" style={{ color: "#64748B" }}>
                <th className="p-4 mono-label font-normal">ID</th>
                <th className="p-4 mono-label font-normal">Name</th>
                <th className="p-4 mono-label font-normal hidden md:table-cell">Department</th>
                <th className="p-4 mono-label font-normal hidden md:table-cell">Designation</th>
                <th className="p-4 mono-label font-normal hidden lg:table-cell">Net Salary</th>
                <th className="p-4 mono-label font-normal">Status</th>
                <th className="p-4 mono-label font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.employee_id} className="border-b border-white/5 hover:bg-[#131C31] transition-colors"
                  data-testid={`employee-row-${e.employee_id}`}>
                  <td className="p-4 font-mono-accent text-cyan" style={{ color: "#00F0FF" }}>{e.employee_id}</td>
                  <td className="p-4 text-white font-medium">{e.personal?.full_name || "—"}</td>
                  <td className="p-4 text-slate-300 hidden md:table-cell">{e.employment?.department || "—"}</td>
                  <td className="p-4 text-slate-300 hidden md:table-cell">{e.employment?.designation || "—"}</td>
                  <td className="p-4 text-slate-300 hidden lg:table-cell">{inr(e.computed?.net_salary)}</td>
                  <td className="p-4">
                    <span className="mono-label px-2 py-1 border" style={{ color: statusColor(e.employment?.employee_status), borderColor: statusColor(e.employment?.employee_status) + "55" }}>
                      {e.employment?.employee_status || "—"}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => navigate(`/portal/employees/${e.employee_id}`)} data-testid={`view-${e.employee_id}`}
                        className="p-2 text-slate-400 hover:text-cyan transition-colors" title="View"><Eye size={16} /></button>
                      {canEdit && (
                        <>
                          <button onClick={() => navigate(`/portal/employees/${e.employee_id}/edit`)} data-testid={`edit-${e.employee_id}`}
                            className="p-2 text-slate-400 hover:text-cyan transition-colors" title="Edit"><Pencil size={16} /></button>
                          <button onClick={() => setDeleteTarget(e)} data-testid={`delete-${e.employee_id}`}
                            className="p-2 text-slate-400 hover:text-red-400 transition-colors" title="Delete"><Trash2 size={16} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0C1322] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete employee?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "#94A3B8" }}>
              This will permanently remove {deleteTarget?.personal?.full_name} ({deleteTarget?.employee_id}) and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete"
              className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
